import type { IFieldOptions } from '@axo/form/feature/form-input';
import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { EmailFieldUI } from '@axo/form/feature/form-input/EmailField/EmailFieldUI';
import { PhoneFieldUI } from '@axo/form/feature/form-input/PhoneField/PhoneFieldUI';
import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import { TextFieldUI } from '@axo/form/feature/form-input/TextField/TextFieldUI';
import { MaritalStatus } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../../../../context/AxoForm/AxoForm';
import { validateNorwegianSSN } from '../../../../../../../../utils/Norway/validateNorwegianSSN';
import { useEmailValidation } from '../../../../../../../../utils/useEmailValidation';
import type {
  CoApplicantDetailsData,
  NorwayFormData,
} from '../../../../../FormData.no.types';
import {
  maritalStatusOptions,
  radioOptions,
} from '../../../../../config/globals';
import { gridColumns } from '../../../../../config/layouts/grid';
import {
  getEmailRegisterOptions,
  phoneFieldOptions,
} from '../../../../shared-config';
import { coApplicantMaritalStatusFieldOptions } from './config';
import { coApplicantIsCohabitantRegisterOptions } from './config/coApplicantIsCohabitant';

export const CoApplicantDetails = () => {
  const { t } = useTranslation();

  const { baseUrl, axoFormData } = useAxoFormContext<NorwayFormData>();

  const { setValue, watch } = useFormContext();

  const [maritalStatus, isCohabitant] = watch([
    'LoanApplication/MaritalStatus',
    'LoanApplication/CoApplicantIsCohabitant',
  ]);

  const { EmailPrompt, formError, enableEmailValidation } = useEmailValidation({
    baseUrl,
    email: watch('CoCustomer/Email'),
    setEmail: (email) => setValue('CoCustomer/Email', email),
  });

  const emailRegisterOptions = getEmailRegisterOptions(enableEmailValidation);

  const mainApplicantSSN = axoFormData['Person/SocialSecurityNumber'];
  const ssnRegisterOptions: IFieldOptions = useMemo(
    () => ({
      registerOptions: {
        required: 'CoApplicantSocialSecurityNumber.error.required',
        setValueAs: (value) => value.trim(),
        validate: (value) => {
          if (!validateNorwegianSSN(value))
            return 'SocialSecurityNumber.error.pattern';

          if (value === mainApplicantSSN)
            return t('CoApplicantSocialSecurityNumber.error.unique');
        },
      },
      tooltipValidation: {
        message: 'CoApplicantSocialSecurityNumber.tooltip',
      },
    }),
    [mainApplicantSSN, t]
  );

  const askCoAppMaritalStatus =
    maritalStatus === MaritalStatus.Single || isCohabitant === 'No';

  return (
    <Grid columns={gridColumns}>
      <RadioFieldUI<CoApplicantDetailsData>
        name="LoanApplication/CoApplicantIsCohabitant"
        label={t('CoApplicantIsCohabitant.label')}
        data-testid="is-cohabitant-radio"
        radioStyle="both"
        options={radioOptions}
        registerOptions={coApplicantIsCohabitantRegisterOptions.registerOptions}
      />

      {/* Empty div for grid */}
      <div />

      <EmailFieldUI<CoApplicantDetailsData>
        name="CoCustomer/Email"
        label={t('CoApplicantEmail.label')}
        data-testid="email-input"
        registerOptions={emailRegisterOptions}
        error={formError}
        confirmationPrompt={<EmailPrompt />}
      />

      <PhoneFieldUI<CoApplicantDetailsData>
        name="CoCustomer/MobilePhoneNumber"
        label={t('MobilePhoneNumber.label', { context: 'coapp' })}
        data-testid="phone-number-input"
        format="*** ** ***"
        {...phoneFieldOptions}
      />

      <TextFieldUI<CoApplicantDetailsData>
        name="CoPerson/SocialSecurityNumber"
        label={t('CoApplicantSocialSecurityNumber.label')}
        data-testid="ssn-input"
        {...ssnRegisterOptions}
      />

      {askCoAppMaritalStatus ? (
        <DropdownFieldUI<CoApplicantDetailsData>
          name="LoanApplication/CoApplicantMaritalStatus"
          label={t('CoApplicantMaritalStatus.label')}
          data-testid="marital-status-dropdown"
          options={maritalStatusOptions}
          {...coApplicantMaritalStatusFieldOptions}
        />
      ) : (
        // Empty div for grid
        <div />
      )}
    </Grid>
  );
};
